import { FilterItemProps } from "components/atoms/Filter";
import {
  OPTIONS_COMPANY,
  OPTIONS_COMPANY_REVENUE,
  OPTIONS_DEPARTMENT,
  OPTIONS_INDUSTRY,
  OPTIONS_JOBTITLE,
  OPTIONS_LEADS_ONLY,
  OPTIONS_LOCATION,
  OPTIONS_SENIORITY,
} from "components/modules/SearchMain/SearchMainHelper";
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getAllPixelLeads, getTotalPixelLeads } from "store/slices/insights";

const PAGE_SIZE = 25;

export const usePulse = ({ formEnrichment }: { formEnrichment: boolean }) => {
  const dispatch = useAppDispatch();
  const { seniorities, departaments, headcount, revenue } = useAppSelector(
    (state) => state.filters
  );
  const { data: user } = useAppSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters: FilterItemProps[] = [
    {
      label: "Lead Information",
      type: "divider",
      name: "company_lead__divider",
      removeBorder: true,
    },
    {
      name: "last_seen",
      icon: "calendar-alt",
      label: "Last Seen",
      placeholder: "Last Seen",
      type: "range",
    },
    {
      name: "person_location",
      icon: "user-location",
      label: "Person Location",
      placeholder: "Enter Locations",
      options: OPTIONS_LOCATION,
      type: "selectable",
    },
    {
      name: "leads",
      icon: "people",
      label: "Leads",
      placeholder: "Enter filters",
      options: OPTIONS_LEADS_ONLY,
      type: "selectable",
    },
    {
      name: "linkedin",
      icon: "linkedin",
      label: "LinkedIn",
      placeholder: "Enter a LinkedIn URL",
      type: "searchable",
    },
    {
      name: "job_titles",
      icon: "job",
      label: "Job Title",
      placeholder: "Enter job titles",
      options: OPTIONS_JOBTITLE,
      type: "selectable",
    },
    {
      name: "seniority",
      icon: "job",
      label: "Seniority",
      placeholder: "Enter seniorities",
      options:
        seniorities.map((item) => ({
          label: item.name,
          value: item.name,
        })) || OPTIONS_SENIORITY,
      type: "selectable",
    },
    {
      label: "Company",
      type: "divider",
      name: "company_divider",
    },
    {
      name: "department",
      icon: "job",
      label: "Department",
      placeholder: "Enter departments",
      options:
        departaments.map((item) => ({
          label: item.name,
          value: item.name,
        })) || OPTIONS_DEPARTMENT,
      type: "selectable",
    },
    {
      name: "industry",
      icon: "industry",
      label: "Industry",
      placeholder: "Enter industries",
      options: OPTIONS_INDUSTRY,
      type: "selectable",
    },
    {
      name: "company_name",
      icon: "company",
      label: "Name",
      placeholder: "Enter companies",
      options: OPTIONS_COMPANY,
      type: "selectable",
    },
    {
      name: "company_location",
      icon: "location",
      label: "Location",
      placeholder: "Enter locations",
      options: OPTIONS_LOCATION,
      type: "selectable",
    },
    {
      name: "company_revenue",
      icon: "revenue",
      label: "Revenue",
      placeholder: "Enter company revenue",
      options:
        revenue.map((item) => ({
          label: item.name,
          value: item.name,
        })) || OPTIONS_COMPANY_REVENUE,
      type: "selectable",
    },
    {
      name: "client_id",
      icon: "user",
      label: "Client Id",
      placeholder: "Enter Client Id",
      type: "searchable",
      hideOn: !user.isCoordinator,
    },
  ];
  const getSearchParamsValues = () => {
    const page = searchParams.get("page")
      ? Number(searchParams.get("page"))
      : 1;
    const locations_person = searchParams.get("person_location") || "";
    const leads_revealed = searchParams.get("leads") || "";
    const linkedin = searchParams.get("linkedin") || "";
    const job_title = searchParams.get("job_titles") || "";
    const seniority = searchParams.get("seniority") || "";
    const department = searchParams.get("department") || "";
    const industry = searchParams.get("industry") || "";
    const company_name = searchParams.get("company_name") || "";
    const company_location = searchParams.get("company_location") || "";
    const revenue_filter = searchParams.get("company_revenue") || "";
    const employee_count = searchParams.get("employee_count") || "";
    const departament_filter = searchParams.get("departament") || "";
    const search_name = searchParams.get("search_name") || "";
    const client_id = searchParams.get("client_id") || "";
    const page_visited = searchParams.get("page_visited") || "";
    const number_pages_viewed = searchParams.get("number_pages_viewed") || "";
    const number_of_visits = searchParams.get("number_of_visits") || "";
    const time_on_page = searchParams.get("time_on_page") || "";
    const lead_score = searchParams.get("lead_score") || "";
    const client_name = searchParams.get("client_name") || "";
    const income_ranges = searchParams.get("income_ranges") || "";
    const genders = searchParams.get("genders") || "";
    const seniorities_filter = searchParams.get("seniorities") || "";
    const last_seen = searchParams.get("last_seen") || "";
    return {
      last_seen,
      page,
      locations_person,
      leads_revealed,
      linkedin,
      job_title,
      seniority,
      department,
      industry,
      company_name,
      company_location,
      revenue_filter,
      employee_count,
      departament_filter,
      search_name,
      client_id,
      page_visited,
      number_pages_viewed,
      number_of_visits,
      time_on_page,
      lead_score,
      client_name,
      income_ranges,
      genders,
      seniorities_filter,
    };
  };

  const {
    last_seen,
    page,
    locations_person,
    leads_revealed,
    linkedin,
    job_title,
    seniority,
    department,
    industry,
    company_name,
    company_location,
    revenue_filter,
    employee_count,
    departament_filter,
    search_name,
    client_id,
    page_visited,
    number_pages_viewed,
    number_of_visits,
    time_on_page,
    lead_score,
    client_name,
    income_ranges,
    seniorities_filter,
    genders,
  } = getSearchParamsValues();

  const prepareParams = useMemo(() => {
    return {
      page,
      size: PAGE_SIZE,
      show_form_data: formEnrichment,
      linkedin,
      seniority,
      department,
      industries: industry,
      locations_person,
      leads_revealed,
      job_titles: job_title,
      company_location,
      revenue: revenue_filter,
      employee_count,
      departments: departament_filter,
      search_name,
      client_id,
      page_visited,
      number_pages_viewed,
      number_of_visits,
      time_on_page,
      lead_score,
      client_name,
      income_ranges,
      seniorities: seniorities_filter,
      genders,
      companies: company_name,
      last_seen,
    };
  }, [
    last_seen,
    formEnrichment,
    page,
    locations_person,
    leads_revealed,
    linkedin,
    job_title,
    seniority,
    department,
    industry,
    company_name,
    company_location,
    revenue_filter,
    employee_count,
    departament_filter,
    search_name,
    client_id,
    page_visited,
    number_pages_viewed,
    number_of_visits,
    time_on_page,
    lead_score,
    client_name,
    income_ranges,
    seniorities_filter,
    genders,
  ]);

  const fetchResults = useCallback(async () => {
    dispatch(getAllPixelLeads(prepareParams));
    dispatch(getTotalPixelLeads(prepareParams));
  }, [prepareParams]);

  useEffect(() => {
    fetchResults();
  }, [formEnrichment, prepareParams]);
  return { filters, pageSize: PAGE_SIZE };
};
